import React, { useMemo } from 'react';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { CodeBlock } from './CodeBlock';
import { MermaidBlock } from './MermaidBlock';
import './RichMarkdown.css';

// Pipeline markdown ricca condivisa (chat PA + artefatti documento):
// GFM + formule (remark-math → KaTeX, throwOnError:false = mai crash su input
// malformato) + code fence con syntax highlighting + fence ```mermaid rese
// come diagramma. Unica fonte di verità del rendering markdown del workspace.

interface RichMarkdownProps {
  children: string;
  /** remark-breaks (newline singolo = <br>), usato dalla chat. */
  breaks?: boolean;
  className?: string;
}

interface MarkdownCodeProps {
  className?: string;
  children?: React.ReactNode;
}

function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  return '';
}

const MarkdownCode: React.FC<MarkdownCodeProps> = ({ className, children }) => {
  const match = /language-([\w+-]+)/.exec(className ?? '');
  const raw = extractText(children);
  const isBlock = Boolean(match) || raw.includes('\n');
  if (!isBlock) {
    return (
      <code className="not-prose rounded bg-muted px-1 py-0.5 font-mono text-[0.85em] text-foreground before:content-none after:content-none">
        {children}
      </code>
    );
  }
  const code = raw.replace(/\n$/, '');
  const language = match?.[1]?.toLowerCase();
  if (language === 'mermaid') {
    return <MermaidBlock code={code} className="my-2" />;
  }
  return <CodeBlock code={code} language={language} className="my-2" />;
};

// `pre` neutro: il wrapper visuale (bordo, sfondo, header) vive in CodeBlock/
// MermaidBlock, altrimenti il pre di prose aggiungerebbe un secondo riquadro.
const MarkdownPre: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;

const MARKDOWN_COMPONENTS: Components = {
  pre: MarkdownPre,
  code: MarkdownCode,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div
      className="not-prose pa-markdown__table overflow-x-auto rounded-lg border border-border"
      role="region"
      aria-label="Tabella Markdown"
      tabIndex={0}
    >
      <table className="min-w-full text-[13px] [&_th]:bg-muted [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_td]:px-3 [&_td]:py-1.5 [&_td]:border-t [&_td]:border-border">
        {children}
      </table>
    </div>
  ),
};

export const RichMarkdown: React.FC<RichMarkdownProps> = ({ children, breaks = false, className }) => {
  const remarkPlugins = useMemo<NonNullable<Options['remarkPlugins']>>(
    () => (breaks ? [remarkGfm, remarkBreaks, remarkMath] : [remarkGfm, remarkMath]),
    [breaks],
  );
  const rehypePlugins = useMemo<NonNullable<Options['rehypePlugins']>>(
    () => [[rehypeKatex, { throwOnError: false, strict: false }]],
    [],
  );
  return (
    <div className={`prose min-w-0 pa-markdown ${className ?? 'max-w-none'}`}>
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={MARKDOWN_COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};
